import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium transition-all duration-150 ease-out disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
  {
    variants: {
      variant: {
        primary: 'bg-ink hover:bg-ink-soft shadow-[0_1px_0_rgba(0,0,0,0.1),0_4px_20px_-4px_rgba(0,0,0,0.15)] active:translate-y-[1px]',
        secondary: 'bg-paper-warm text-ink border border-ink-faint hover:bg-paper-card active:translate-y-[1px]',
        ghost: 'text-ink-muted hover:text-ink underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-[48px] px-6 rounded-lg text-base',
        sm: 'min-h-[40px] px-4 rounded-md text-sm',
        lg: 'min-h-[56px] px-8 rounded-full text-body font-medium w-full sm:w-auto shadow-[0_18px_38px_-18px_rgba(26,26,26,0.55)] active:translate-y-[1px] active:scale-[0.99]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  style,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  // Force white text for primary variant since Tailwind v4 CSS variable syntax isn't working
  const buttonStyle = variant === 'primary' || variant === undefined
    ? { color: '#fafaf8', ...style }
    : style

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      style={buttonStyle}
      {...props}
    />
  )
}

export { Button, buttonVariants }
